// @givmo/board — EVERY word CBG-RC10's screens add: a closed session's own minutes approved by the
// signatures of the directors who were in it (D-CBG-24). The board's closed-session rule names the way
// (`directors_present`, recorded on Meeting rules); an officer asks for the signatures, each director
// who was there signs in their own session or an officer records it, and the last signature on the
// current bytes approves them (backend `board_minutes.md` §S1–§S16).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them: copy round CBG-RC10-COPY1's page,
// `scratchpad/2026-10-14-rc10-copy-round/copy-review.html`, approved as drafted (Jonathan,
// 2026-10-14 09:53 PDT, "go with your recs"), each line beside the frame that drew it (`c2`, `d5`,
// `c3`, `g3`, `g4`; the design branch `design/board-minutes-approval-closed-sessions` @ `8d5c8ee8`,
// approved for building 2026-10-12 22:52 PDT) or marked "not drawn". The count line's singular
// ("1 of 4 has signed.") is plain agreement with the approved line, added in the cut's fold
// (review-139's P1). A line an approved page already says is reused from where it lives, never
// restated here: RC11's record-not-decisions line (`minutes-rule-words.ts`), the closed minutes'
// public name (`closedMinutesOfTheMeeting`), the minutes signature's refused-twice line, "Record a
// signature", "Record it", "Sign" and the on-behalf entry's provenance note.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what the board's rule is and what Board recorded, never what
// the law requires, never "must" or "should". A person is named in full, as the minutes record them,
// and no pronoun stands for a named person. The approval is the act of the directors who signed, never
// of the one whose signature came last (CBG-UX3).
//
// ⚠ This is never a written consent (D-CBG-24): no line here calls it one, or says "every director".

/** Why a seat in the session wasn't asked to sign (backend `BoardMinutesNotAskedOut.reason`). */
export type NotAskedReason = 'stepped_out' | 'not_a_director';

/** One person the ask didn't ask, with the reason the request recorded. */
export interface NotAskedPerson {
  name: string;
  reason: NotAskedReason;
}

/**
 * "{k} of {n} have signed.", with the verb agreeing when one has (review-139's P1, plain agreement
 * with the approved line: "1 of 4 has signed.").
 */
function signedOf(signedCount: number, signerCount: number): string {
  return `${signedCount} of ${signerCount} ${signedCount === 1 ? 'has' : 'have'} signed.`;
}

export const minutesSignaturesWords = {
  // ── The rule's way, explained (Meeting rules' card and the minutes page's "When and why") ──────

  /** Drawn, d5 (the director's lead) and the harness's m-frames' way line. */
  wayLine:
    'Your board approves a closed session’s minutes by the signatures of the directors who were there.',
  /**
   * Drawn, g4 (the card's "When and why"): its three lines as drawn, then the
   * one record-not-decisions line every approval surface says (RC11's rule; not drawn here).
   */
  wayWhy: (recordNotDecisions: string): readonly string[] => [
    'Use this when your board has adopted it, in its bylaws or by a resolution.',
    'It isn’t a written consent of the board, which every director signs: here, each director who was in the session signs these minutes, and when the last one signs, they’re approved.',
    'Any director may still open and read them.',
    recordNotDecisions,
  ],

  // ── The officer's minutes page (frame c2) ──────────────────────────────────────────────────────

  /** Not drawn: the action while no request is out (one press asks). */
  askForTheirSignatures: 'Ask for their signatures',
  /** Not drawn: the toast once the ask is recorded. Nothing is mailed. */
  asked: 'They’re out for signatures.',
  /**
   * Drawn, c2 ("Out for the signatures of the 4 directors who were there. 2
   * of 4 have signed."); the one-director form is not drawn.
   */
  outLine: (signerCount: number, signedCount: number): string =>
    signerCount === 1
      ? `Out for the signature of the director who was there. ${signedCount} of 1 has signed.`
      : `Out for the signatures of the ${signerCount} directors who were there. ${signedOf(signedCount, signerCount)}`,
  /** Drawn, c2 (the small line under the out line). */
  outWarning: 'If these minutes change before the last signature, everyone signs again.',
  /**
   * Not drawn: who the minutes record as in the session changed since the
   * ask, so the request can't complete (the contract's §S7); the officer's page says what to do.
   */
  sessionChanged:
    'Who these minutes record as in this session has changed since the signatures were asked for. Withdraw the request, then ask again.',
  /** Not drawn: the quiet action beside the request's line. */
  withdrawTheRequest: 'Withdraw the request',
  /**
   * Not drawn: the Signatures card before the ask, in place of "A signature
   * follows the board’s approval", which isn't true of this way.
   */
  signaturesBeforeAsk:
    'The directors who were there sign here to approve these minutes once you ask for their signatures.',

  // The Signatures card while the request is out (c2).

  /** Drawn, c2 ("Signed to approve · Feb 15 at 4:12 PM · confirmed with a
   *  passkey"), and the after-approval list's first rows. */
  signedToApprove: (when: string | null, capture: string | null): string =>
    ['Signed to approve', when, capture]
      .filter((part) => part !== null && part.length > 0)
      .join(' · '),
  /** Not drawn: a signature on bytes that changed since. */
  signedEarlierVersion: (when: string | null): string =>
    when === null ? 'Signed an earlier version' : `Signed an earlier version · ${when}`,
  /** Drawn, c2. */
  notSignedYet: 'Not signed yet',
  /**
   * Drawn, c2 ("Not asked: Theo Barrett, who stepped out for a conflict of
   * interest, and Maren Lindqvist, who was in the session and isn’t a director."). Each person named
   * in full, as the minutes record them, with the reason the request recorded.
   */
  notAsked: (people: readonly NotAskedPerson[]): string => {
    const each = people.map((person) =>
      person.reason === 'stepped_out'
        ? `${person.name}, who stepped out for a conflict of interest`
        : `${person.name}, who was in the session and isn’t a director`,
    );
    // Each name carries its own clause, so the last joins after a comma, as c2 draws it.
    const listed =
      each.length <= 1 ? (each[0] ?? '') : `${each.slice(0, -1).join(', ')}, and ${each.at(-1)}`;
    return `Not asked: ${listed}.`;
  },

  // The withdrawal dialog (not drawn).

  /** Not drawn. */
  withdrawTitle: 'Withdraw the request for signatures?',
  /** Not drawn. */
  withdrawBody:
    'The signatures already given stay in the minutes’ history and count for nothing. You can ask again.',
  /** Not drawn: the required reason. */
  withdrawWhy: 'Why',
  /** Not drawn. */
  withdrawConfirm: 'Withdraw it',
  /** Not drawn: the toast. */
  withdrawn: 'The request is withdrawn.',

  // The officer's entry of one director's signature (not drawn).

  /** Not drawn: the dialog's title. */
  onBehalfTitle: 'Record a signature to approve',
  /** Not drawn: on the pattern of the minutes signature's entry. */
  onBehalfBody:
    'This records that one of the directors who were there signed to approve these minutes somewhere other than here — on paper, or in a message to you. It is recorded as your entry on their behalf, never as them signing in their own session.',
  /** Not drawn: the asked directors who haven't signed this version. */
  whichDirector: 'Which director signed',
  /** Not drawn: the select's empty choice. */
  chooseDirector: 'Choose a director',
  /** Not drawn: the required provenance, named as the contract names it. */
  whereFrom: 'Where this signature came from',
  /** Not drawn: the fingerprint the entry names. */
  namesTheseBytes: 'It names these minutes by their fingerprint',

  // The approval the last signature made (not drawn: the approved state).

  /**
   * Not drawn: the minutes page's approved line, as the backend's
   * `approved_by_signatures_line` says it (`board_minutes_signatures.py`). The act of the directors
   * who signed, never the one whose signature came last (CBG-UX3).
   */
  approvedLine: (signedCount: number | null, lastOn: string): string =>
    signedCount === 1
      ? `Approved by the signature of the director who was in this session, on ${lastOn}.`
      : signedCount !== null && signedCount > 1
        ? `Approved by the signatures of the ${signedCount} directors who were in this session; the last on ${lastOn}.`
        : `Approved by the signatures of the directors who were in this session; the last on ${lastOn}.`,
  /** Not drawn: the record page's approval line, its moment written out. */
  recordApproval: (signedCount: number | null, when: string): string =>
    signedCount === 1
      ? `Approved by the signature of the director who was in this session, on ${when}`
      : signedCount !== null && signedCount > 1
        ? `Approved by the signatures of the ${signedCount} directors who were in this session; the last on ${when}`
        : `Approved by the signatures of the directors who were in this session; the last on ${when}`,

  // ── The record: the minute book and its history (frame c3) ──────────────────────────────────────

  /** Drawn, c3 ("Minutes · Feb 11, 2027 · Out for signatures"). */
  outForSignatures: 'Out for signatures',
  /** Drawn, c3 (the history's rows). */
  signedToApproveEvent: 'Signed to approve',
  /** Not drawn: the approval event the last signature made, its title. */
  approvalEventTitle: 'Approved by the signatures of the directors who were there',
  /** Not drawn: whose signature was the last (a director's own). */
  lastSignature: (seat: string) => `The last signature was ${seat}’s`,
  /** Not drawn: an officer recorded the last signature for the seat. */
  lastSignatureOnBehalf: (seat: string, officer: string) =>
    `The last signature was ${seat}’s, recorded by ${officer}`,
  /** Not drawn: the last seat's name isn't shown. */
  lastSignatureUnnamed: 'The last signature was by someone whose name isn’t shown',

  // ── The director (frame d5) and the director's boards ───────────────────────────────────────────

  /** Drawn, d5: the lead with the count. */
  directorLead: (signerCount: number, signedCount: number): string =>
    signerCount === 1
      ? `Your board approves a closed session’s minutes by the signatures of the directors who were there. ${signedCount} of 1 has signed.`
      : `Your board approves a closed session’s minutes by the signatures of the directors who were there. ${signedOf(signedCount, signerCount)}`,
  /** Not drawn: this director signed the current version. */
  youSigned: (when: string | null) =>
    when === null
      ? 'You signed to approve these minutes.'
      : `You signed to approve these minutes on ${when}.`,
  /** Not drawn: the last signature, theirs, approved the minutes. */
  youSignedLast: 'You signed, and that was the last signature: these minutes are approved.',
  /**
   * Not drawn: this director signed an earlier version; the attention item's
   * second line too (the contract's §S9).
   */
  signTheCurrentVersion: 'These minutes changed after you signed. Sign the current version.',
  /**
   * Not drawn: who the minutes record as in the session changed since the ask
   * (the backend's own sentence for the refusal, §S7).
   */
  directorSessionChanged:
    'Who these minutes record as in this session has changed since the signatures were asked for. An officer withdraws the request and asks again.',
  /**
   * Not drawn: the attention row on the director's boards, the minutes named
   * by public facts only (RC11's approved `closedMinutesOfTheMeeting`).
   */
  attentionRow: (closedMinutes: string) => `Sign to approve ${closedMinutes}`,
  /**
   * CBG-FIX74, approved 2026-10-17 22:51 PDT (design-225's copy page, row 1.1; frames b1, c1, m1): the
   * same row while the signing window is shut, in the ask email's own words (CBG-RC10-COPY2:
   * "The {Org} board has minutes waiting for your signature.", CBG-FIX143's house form). Nothing of
   * the session: no meeting, date, purpose, count, "closed" or "approve" (the email's rule, the
   * backend's §S17.3).
   */
  minutesWaiting: 'Minutes waiting for your signature',
} as const;
